import React, { useRef, useState, useEffect } from 'react';
import { useDarkMode } from '../../context/DarkModeContext';
import { motion, AnimatePresence } from 'framer-motion';
import AchievementsTab from './AchievementsTab';
import MetricsCards from './Metrics/MetricsCards';
import ReviewsTab from './ReviewsTab';
import { useAchievements } from '../../hooks/useAchievements';

interface TabContainerProps {
  activeTab: 'goals' | 'competencies' | 'achievements' | 'metrics' | 'reviews';
  onTabChange: (tab: 'goals' | 'competencies' | 'achievements' | 'metrics' | 'reviews') => void;
  focus: any;
  goals: any[];
  competencies: any[];
  cards: any[] | null;
  onRefresh: () => void;
  onQuickAdd: (context?: any) => void;
  onPrepareReview?: () => void;
}

const TabContainer: React.FC<TabContainerProps> = ({
  activeTab,
  onTabChange,
  focus,
  goals,
  competencies,
  cards,
  onRefresh,
  onQuickAdd,
  onPrepareReview
}) => {
  const { darkMode } = useDarkMode();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(false);
  
  // Get achievements count for this specific focus
  const { achievements } = useAchievements(focus?.id);
  
  const cardBg = darkMode ? 'bg-gray-800' : 'bg-white';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const activeBg = darkMode ? 'bg-gray-700' : 'bg-gray-100';

  const tabs = [
    { id: 'goals' as const, label: 'Goals', count: goals?.length || 0 },
    { id: 'competencies' as const, label: 'Competencies', count: competencies?.length || 0 },
    { id: 'achievements' as const, label: 'Achievements', count: achievements?.length || 0 },
    { id: 'metrics' as const, label: 'Metrics', count: null },
    { id: 'reviews' as const, label: 'Reviews', count: null }
  ];

  // Check scroll position for gradient indicators
  const checkScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setShowLeftGradient(scrollLeft > 0);
    setShowRightGradient(scrollLeft < scrollWidth - clientWidth - 5);
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      // Check initial state
      checkScroll();
      // Check after a brief delay to ensure proper layout
      setTimeout(checkScroll, 100);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', checkScroll);
      }
    };
  }, []);

  // Scroll active tab into view
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const activeTabIndex = tabs.findIndex(tab => tab.id === activeTab);
    if (activeTabIndex === -1) return;

    // Find the button element
    const buttons = container.querySelectorAll('button');
    const activeButton = buttons[activeTabIndex];
    
    if (activeButton) {
      // Scroll the active button into view with smooth animation
      activeButton.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'nearest',
        inline: 'center' 
      });
      
      // Check scroll position after animation
      setTimeout(checkScroll, 300);
    }
  }, [activeTab]);

  return (
    <div className={`${cardBg} ${borderColor} border rounded-lg`}>
      {/* Tab Headers */}
      <div className="relative border-b border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Left gradient indicator */}
        {showLeftGradient && (
          <div className={`absolute left-0 top-0 bottom-0 w-8 pointer-events-none z-10 bg-gradient-to-r ${
            darkMode ? 'from-gray-800' : 'from-white'
          } to-transparent`} />
        )}
        
        {/* Right gradient indicator */}
        {showRightGradient && (
          <div className={`absolute right-0 top-0 bottom-0 w-8 pointer-events-none z-10 bg-gradient-to-l ${
            darkMode ? 'from-gray-800' : 'from-white'
          } to-transparent`} />
        )}
        
        <div 
          ref={scrollContainerRef}
          className="flex overflow-x-auto tab-scroll-container"
          style={{
            WebkitOverflowScrolling: 'touch' /* Smooth scrolling on iOS */
          }}
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`
                flex-shrink-0 px-4 py-3 text-sm font-medium transition-colors relative whitespace-nowrap
                ${activeTab === tab.id 
                  ? textColor 
                  : mutedTextColor + ' hover:text-gray-700 dark:hover:text-gray-300'
                }
              `}
            >
              <span>{tab.label}</span>
              {tab.count > 0 && (
                <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                  activeTab === tab.id
                    ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                }`}>
                  {tab.count}
                </span>
              )}
              {activeTab === tab.id && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-600"
                />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        <AnimatePresence mode="wait">
          {activeTab === 'goals' && (
            <motion.div
              key="goals"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className={`text-lg font-medium ${textColor}`}>Performance Goals</h3>
                <button
                  onClick={() => onQuickAdd({ 
                    title: 'Add Goal Achievement',
                    placeholder: 'What progress have you made on your goals?'
                  })}
                  className="text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300"
                >
                  + Add Progress
                </button>
              </div>
              
              {goals && goals.length > 0 ? (
                <div className="space-y-3">
                  {goals.map((goal) => (
                    <div
                      key={goal.id}
                      className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}
                    >
                      <h4 className={`font-medium ${textColor}`}>{goal.goal_text}</h4>
                      <p className={`text-sm ${mutedTextColor} mt-1`}>
                        Type: {goal.goal_type || 'Custom'}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className={`text-center py-8 ${mutedTextColor}`}>
                  No goals defined yet. Goals will appear here once set up.
                </p>
              )}
            </motion.div>
          )}

          {activeTab === 'competencies' && (
            <motion.div
              key="competencies"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className={`text-lg font-medium ${textColor}`}>Competency Framework</h3>
                <button
                  onClick={onRefresh}
                  className="text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300"
                >
                  Refresh
                </button>
              </div>
              
              {competencies && competencies.length > 0 ? (
                <div className="space-y-3">
                  {competencies.map((competency) => (
                    <div
                      key={competency.id}
                      className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className={`font-medium ${textColor}`}>{competency.name}</h4>
                          <p className={`text-sm ${mutedTextColor} mt-1`}>
                            {competency.description}
                          </p>
                        </div>
                        <div className={`ml-4 px-3 py-1 rounded-full text-sm font-medium ${
                          darkMode ? 'bg-purple-900/30' : 'bg-purple-100'
                        } text-purple-600 dark:text-purple-400`}>
                          {competency.weight}%
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className={`text-center py-8 ${mutedTextColor}`}>
                  No competencies defined yet. Competencies will appear here once configured.
                </p>
              )}
            </motion.div>
          )}

          {activeTab === 'achievements' && (
            <AchievementsTab
              focus={focus}
              onQuickAdd={onQuickAdd}
            />
          )}

          {activeTab === 'metrics' && (
            <motion.div
              key="metrics"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <MetricsCards focusId={focus.id} />
            </motion.div>
          )}

          {activeTab === 'reviews' && (
            <ReviewsTab
              focus={focus}
              onPrepareReview={onPrepareReview || (() => {})}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default TabContainer;